import { supabase } from "~/integrations/supabase/client";

import type {
  Budget,
  BudgetFilters,
  BudgetFormData,
  BudgetLine,
  BudgetLineInput,
} from "~/types/contabilidad";

export const budgetService = {
  async getAll(filters?: BudgetFilters): Promise<Budget[]> {
    let query = supabase
      .from("budgets")
      .select("*, company:company_id(*), budget_lines(account_id)")
      .eq("is_deleted", false);

    if (filters?.company_id) {
      query = query.eq("company_id", filters.company_id);
    }

    if (filters?.fiscal_year) {
      query = query.eq("fiscal_year", filters.fiscal_year);
    }

    if (filters?.status) {
      query = query.eq("status", filters.status);
    }

    const { data, error } = await query.order("fiscal_year", {
      ascending: false,
    });

    if (error) throw error;

    return (data || []).map((row) => {
      const { budget_lines, ...budget } = row as Budget & {
        budget_lines: Array<{ account_id: string }>;
      };
      return {
        ...budget,
        lines_count: new Set((budget_lines || []).map((l) => l.account_id))
          .size,
      };
    });
  },

  async getById(id: string): Promise<Budget> {
    const { data, error } = await supabase
      .from("budgets")
      .select("*, company:company_id(*)")
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (error) throw error;
    return data as Budget;
  },

  /**
   * All budgets (scenarios) for a company + year. Approved first, then by
   * name — several budgets may exist per company+year for scenario planning.
   */
  async getBudgetsByCompanyYear(
    companyId: string,
    fiscalYear: number
  ): Promise<Budget[]> {
    const { data, error } = await supabase
      .from("budgets")
      .select("*")
      .eq("company_id", companyId)
      .eq("fiscal_year", fiscalYear)
      .eq("is_deleted", false)
      .order("status", { ascending: true }) // 'approved' < 'draft'
      .order("name", { ascending: true });

    if (error) throw error;
    return (data || []) as Budget[];
  },

  async create(formData: BudgetFormData): Promise<Budget> {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from("budgets")
      .insert({
        ...formData,
        created_by: user?.id ?? null,
      })
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as Budget;
  },

  async update(id: string, formData: Partial<BudgetFormData>): Promise<Budget> {
    const { data, error } = await supabase
      .from("budgets")
      .update(formData)
      .eq("id", id)
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as Budget;
  },

  async approve(id: string): Promise<Budget> {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from("budgets")
      .update({
        status: "approved",
        approved_by: user?.id ?? null,
        approved_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as Budget;
  },

  async reopen(id: string): Promise<Budget> {
    const { data, error } = await supabase
      .from("budgets")
      .update({
        status: "draft",
        approved_by: null,
        approved_at: null,
      })
      .eq("id", id)
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as Budget;
  },

  async softDelete(id: string): Promise<void> {
    const { error } = await supabase
      .from("budgets")
      .update({ is_deleted: true })
      .eq("id", id);

    if (error) throw error;
  },

  async getLines(budgetId: string): Promise<BudgetLine[]> {
    const { data, error } = await supabase
      .from("budget_lines")
      .select("*, account:account_id(id, company_id, code, name, account_type)")
      .eq("budget_id", budgetId);

    if (error) throw error;
    return (data || []) as unknown as BudgetLine[];
  },

  /**
   * Batch-save grid edits: upsert changed cells and delete removed accounts.
   * Blocked by the budget_lines_lock_approved trigger when status = approved.
   */
  async saveLines(
    budgetId: string,
    changedCells: BudgetLineInput[],
    removedAccountIds: string[]
  ): Promise<void> {
    if (removedAccountIds.length > 0) {
      const { error } = await supabase
        .from("budget_lines")
        .delete()
        .eq("budget_id", budgetId)
        .in("account_id", removedAccountIds);

      if (error) throw error;
    }

    if (changedCells.length > 0) {
      const { error } = await supabase.from("budget_lines").upsert(
        changedCells.map((cell) => ({
          budget_id: budgetId,
          account_id: cell.account_id,
          month: cell.month,
          amount: cell.amount,
        })),
        { onConflict: "budget_id,account_id,month" }
      );

      if (error) throw error;
    }
  },

  /**
   * Excel import: full replace — delete all lines, insert non-zero cells.
   */
  async replaceLines(
    budgetId: string,
    lines: BudgetLineInput[]
  ): Promise<void> {
    const { error: deleteError } = await supabase
      .from("budget_lines")
      .delete()
      .eq("budget_id", budgetId);

    if (deleteError) throw deleteError;

    const nonZero = lines.filter((line) => line.amount !== 0);
    if (nonZero.length === 0) return;

    const BATCH_SIZE = 500;
    for (let i = 0; i < nonZero.length; i += BATCH_SIZE) {
      const batch = nonZero.slice(i, i + BATCH_SIZE);
      const { error } = await supabase.from("budget_lines").insert(
        batch.map((cell) => ({
          budget_id: budgetId,
          account_id: cell.account_id,
          month: cell.month,
          amount: cell.amount,
        }))
      );

      if (error) throw error;
    }
  },
};
